// apps/client/src/game/interactions/shop.ts (M1-T14) — the shop door (both ways), the counter, the loan desk and the drop box.
// Ranges match the server's checks: doors tuning.town.doorRange from the door tile centre, the counter interactRange from its
// rectangle, the loan desk interactRange from its tile centre, the drop box dropBoxRange from its tile centre.
// Outside opening hours the door reads "Closed" and A says when the shop opens (M1 playtest: it read "Enter" and the press bounced).
import { tuning } from '@whiskerton/content';
import { townTime, type TileXY, type Vec2 } from '@whiskerton/shared';
import { isShopOpen } from '@whiskerton/shared/rules/shop';
import type { Candidate, InteractCtx, Interaction } from '../interact';
import { useGame } from '../../state/store';
import { hourText } from '../../ui/screens/shop';
import { shopLayout } from '../../world/spaces/shop';

const centre = (t: TileXY): Vec2 => ({ x: t.x + 0.5, y: t.y + 0.5 });
const dist = (a: Vec2, b: Vec2) => Math.hypot(a.x - b.x, a.y - b.y);
const counterRect = () => { const [x, y, w, h] = tuning.spaces.shop.counter; return { x, y, w, h }; };
const distToCounter = (p: Vec2) => { const r = counterRect(); return Math.hypot(Math.max(r.x - p.x, 0, p.x - (r.x + r.w)), Math.max(r.y - p.y, 0, p.y - (r.y + r.h))); };

const enter = (at: Vec2): Candidate => ({ id: 'shop:enter', at, verb: 'hud.verbEnter', priority: 70 });
const closed = (at: Vec2): Candidate => ({ id: 'shop:closed', at, verb: 'shop.verbClosed', priority: 70 });
/** Open by the synced town clock (the server has the last word: without a town yet, the door is tried). */
function shopOpen(ctx: InteractCtx): boolean {
  const tz = ctx.store.town?.timeZone;
  return !tz || isShopOpen(townTime(ctx.game.socket?.clock.now() ?? Date.now(), tz));
}
const door = (ctx: InteractCtx, at: Vec2): Candidate => (shopOpen(ctx) ? enter(at) : closed(at));
const dropBox = (at: Vec2): Candidate => ({ id: 'shop:dropbox', at, verb: 'shop.verbDropBox', priority: 60 });
const exit = (at: Vec2): Candidate => ({ id: 'shop:exit', at, verb: 'shop.verbExit', priority: 40 });
const counter = (at: Vec2): Candidate => ({ id: 'shop:counter', at, verb: 'shop.verbCounter', priority: 80 });
const loan = (at: Vec2): Candidate => ({ id: 'shop:loan', at, verb: 'shop.verbLoan', priority: 90 });

export const interaction: Interaction = {
  id: 'shop',
  find(ctx) {
    const c: Candidate[] = [];
    const sites = ctx.store.map?.sites;
    if (ctx.space === 'out' && sites) {
      if (dist(ctx.pos, centre(sites.shopDoor)) <= tuning.town.doorRange) c.push(door(ctx, centre(sites.shopDoor)));
      if (dist(ctx.pos, centre(sites.dropBox)) <= tuning.town.dropBoxRange) c.push(dropBox(centre(sites.dropBox)));
    }
    if (ctx.space === 'shop') {
      const l = shopLayout(), door = { x: l.door.x, y: l.door.z }, desk = { x: l.loanDesk.x, y: l.loanDesk.z };
      if (dist(ctx.pos, door) <= tuning.town.doorRange) c.push(exit(door));
      if (distToCounter(ctx.pos) <= tuning.town.interactRange) c.push(counter({ x: l.counter.x, y: l.counter.z }));
      if (dist(ctx.pos, desk) <= tuning.town.interactRange) c.push(loan(desk));
    }
    return c.sort((a, b) => b.priority - a.priority)[0] ?? null;
  },
  run(ctx, c) { runCandidate(ctx, c); },
  /** A tap on the shop or its door, the drop box, the counter, the loan desk or the way out: walk there, then run it. `at` is where
   *  to stand (interiors walk straight to that tile, so it is a free tile in front of the furniture). */
  fromTap(ctx, hit) {
    const t = hit.tile, sites = ctx.store.map?.sites;
    const on = (a: TileXY) => a.x === t.x && a.y === t.y;
    if (ctx.space === 'out' && sites) {
      if (on(sites.shopDoor) || hit.object?.catalogId === 'w.shop') return door(ctx, centre(sites.shopDoor));
      if (on(sites.dropBox) || hit.object?.kind === 'dropbox') return dropBox(centre(sites.dropBox));
      return null;
    }
    if (ctx.space !== 'shop') return null;
    const s = tuning.spaces.shop, [cx, cy, cw, ch] = s.counter;
    if (t.x >= cx && t.x < cx + cw && t.y >= 0 && t.y < cy + ch) return counter({ x: t.x + 0.5, y: cy + ch + 0.5 });
    if (on({ x: s.loanDesk[0], y: s.loanDesk[1] })) return loan({ x: s.loanDesk[0] - 0.5, y: s.loanDesk[1] + 0.5 });
    if (on({ x: s.door[0], y: s.door[1] })) return exit(centre(t));
    return null;
  },
};

function runCandidate(ctx: InteractCtx, c: Candidate): void {
  const open = useGame.getState().openScreen;
  if (c.id === 'shop:enter') void ctx.game.cmd.enterSpace('shop');
  if (c.id === 'shop:closed') useGame.getState().toast('shop.closedToast', { open: hourText(tuning.shop.openHour), close: hourText(tuning.shop.closeHour) });
  if (c.id === 'shop:exit') void ctx.game.cmd.enterSpace('out');
  if (c.id === 'shop:counter') open('shop', undefined);
  if (c.id === 'shop:loan') open('loan', { at: 'shop' });
  if (c.id === 'shop:dropbox') open('sell', { where: 'dropbox' });
}
